import Alpine from 'alpinejs';
import {
    createAuthenticatedReadRecovery,
    handleAuthenticationRequiredResponse,
    readJsonResponse
} from '../../utils/authResponse.js';

Alpine.store('reservations', {
    isFetched: false,
    isLoading: false,
    upcoming: [],
    past: [],
    error: null,
    loginRecovery: null,

    async init() {
        this.initLoginRecovery();

        // The user store starts first. Await its bounded hydration/recovery
        // without starting another profile read or abandoning a valid retry.
        const userStore = Alpine.store('user');
        const ready = await userStore?.whenReady();
        if (
            ready &&
            userStore.isFetched &&
            userStore.isLoggedIn &&
            !this.isFetched &&
            !this.isLoading
        ) {
            await this.fetchReservations();
        }
    },

    initLoginRecovery() {
        if (this.loginRecovery) return;

        const userStore = Alpine.store('user');
        // loginSucceeded fires before the sign-in modal's delayed user refresh,
        // so refresh user state before attempting the one permitted refetch.
        this.loginRecovery = createAuthenticatedReadRecovery({
            refreshUser: () => userStore.fetchUser(),
            isLoggedIn: () => userStore.isLoggedIn,
            refetch: async () => {
                this.isFetched = false;
                await this.fetchReservations();
            }
        });

        window.htmx?.on('loginSucceeded', () =>
            this.loginRecovery.handleLogin()
        );
    },

    async fetchReservations() {
        if (this.isLoading) return;

        this.isLoading = true;
        this.error = null;

        try {
            console.log('Fetching reservations from API...');
            const response = await fetch(
                '/actions/avari/account/reservations',
                {
                    credentials: 'include'
                }
            );

            console.log('Reservations API response status:', response.status);
            const data = await readJsonResponse(response);

            if (
                handleAuthenticationRequiredResponse(response, data, {
                    userStore: Alpine.store('user'),
                    modalsStore: Alpine.store('modals')
                })
            ) {
                // Remove protected data immediately and arm, but do not run,
                // the single safe read retry for the next successful login.
                this.upcoming = [];
                this.past = [];
                this.isFetched = false;
                this.loginRecovery?.request();
                return;
            }

            if (!response.ok) {
                throw new Error(`HTTP error ${response.status}`);
            }

            if (!data) {
                throw new Error(
                    'Reservations API returned an invalid response'
                );
            }

            console.log('Reservations API response data:', data);

            if (data.success) {
                this.upcoming = data.upcoming || [];
                this.past = data.past || [];
                this.isFetched = true;
                console.log('Reservations loaded:', {
                    upcoming: this.upcoming.length,
                    past: this.past.length
                });
            } else {
                throw new Error('Failed to fetch reservations');
            }
        } catch (error) {
            console.error('Error fetching reservations:', error);
            this.error = "We couldn't load your stays. Please try again.";
            this.isFetched = true; // Mark as fetched even on error to show error state
        } finally {
            this.isLoading = false;
        }
    },

    parseDateOnly(value) {
        if (typeof value !== 'string') return null;
        const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!match) return null;

        const year = Number(match[1]);
        const month = Number(match[2]) - 1;
        const day = Number(match[3]);
        return new Date(year, month, day);
    },

    // Format date for display (e.g., "Jan 26 - 29, 2026")
    formatDateRange(checkIn, checkOut) {
        if (!checkIn || !checkOut) return '';

        const options = { month: 'short', day: 'numeric' };
        const yearOptions = { year: 'numeric' };

        const startDate = this.parseDateOnly(checkIn);
        const endDate = this.parseDateOnly(checkOut);
        if (!startDate || !endDate) return '';

        const startStr = startDate.toLocaleDateString('en-US', options);
        const endDay = endDate.getDate();
        const endYear = endDate.toLocaleDateString('en-US', yearOptions);

        // If same month, show: "Jan 26 - 29, 2026"
        // If different months, show: "Jan 26 - Feb 2, 2026"
        if (startDate.getMonth() === endDate.getMonth()) {
            return `${startStr} - ${endDay}, ${endYear}`;
        } else {
            const endStr = endDate.toLocaleDateString('en-US', options);
            return `${startStr} - ${endStr}, ${endYear}`;
        }
    }
});
